// src/services/venuesService.js
/**
 * Venues Service - Centralized service layer for Venues Toolkit
 * 
 * Provides:
 * - Market/Venue CRUD operations
 * - Data caching and optimization
 * - Real-time subscription management
 * - Error handling and retry logic
 * - Batch operations
 * - Analytics integration
 */

import { db, storage } from './firebase';
import {
    collection,
    doc,
    query,
    where,
    orderBy,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    Timestamp,
    serverTimestamp,
    writeBatch,
    limit,
    startAfter
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS & CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const COLLECTIONS = {
    VENUES: 'venues',
    VENUE_SHIFTS: 'venueShifts',
    VENUE_STOCK: 'venueStock',
    MARKET_INSTANCES: 'marketInstances',
    STAFF_AVAILABILITY: 'staffAvailability',
    AVAILABILITY_REQUESTS: 'availabilityRequests',
    AVAILABILITY_RESPONSES: 'availabilityResponses',
    PROMOTIONS: 'promotions',
    PROMOTION_SALES: 'promotion_sales',
    STOCK_HISTORY: 'stockHistory', // Subcollection under venues
    SHIFT_INVITES: 'invites' // Subcollection under venueShifts
};

export const VENUE_STATUS = {
    OPEN: 'open',
    CLOSED: 'closed',
    CANCELLED: 'cancelled',
    PLANNED: 'planned',
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    ARCHIVED: 'archived'
};

export const VENUE_TYPE = {
    FARMERS_MARKET: 'Farmers Market',
    FOOD_FESTIVAL: 'Food Festival',
    POP_UP: 'Pop-up',
    PRIVATE_EVENT: 'Private Event',
    TRADE_SHOW: 'Trade Show',
    REGULAR_MARKET: 'Regular Market',
    OTHER: 'Other'
};

export const RECURRENCE_PATTERN = {
    NONE: 'none',
    DAILY: 'daily',
    WEEKLY: 'weekly',
    BI_WEEKLY: 'bi-weekly',
    MONTHLY: 'monthly',
    CUSTOM: 'custom'
};

export const DAYS_OF_WEEK = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6
};

export const SHIFT_STATUS = {
    OPEN: 'open',
    CONFIRMED: 'confirmed',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled'
};

export const INVITATION_STATUS = {
    REQUESTED: 'requested',
    CONFIRMED: 'confirmed',
    DECLINED: 'declined',
    WITHDRAWN: 'withdrawn'
};

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// ═══════════════════════════════════════════════════════════════════════════════
// CACHE MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

class CacheManager {
    constructor() {
        this.cache = new Map();
        this.timestamps = new Map();
    }

    set(key, value) {
        this.cache.set(key, value);
        this.timestamps.set(key, Date.now());
    }

    get(key) {
        if (!this.cache.has(key)) return null;
        
        const timestamp = this.timestamps.get(key);
        if (Date.now() - timestamp > CACHE_TTL) {
            this.delete(key);
            return null;
        }
        
        return this.cache.get(key);
    }

    delete(key) {
        this.cache.delete(key);
        this.timestamps.delete(key);
    }

    clear() {
        this.cache.clear();
        this.timestamps.clear();
    }

    has(key) {
        if (!this.cache.has(key)) return false;
        const timestamp = this.timestamps.get(key);
        if (Date.now() - timestamp > CACHE_TTL) {
            this.delete(key);
            return false;
        }
        return true;
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

class SubscriptionManager {
    constructor() {
        this.subscriptions = new Map();
    }

    add(key, unsubscribe) {
        // Unsubscribe from existing subscription if any
        if (this.subscriptions.has(key)) {
            this.subscriptions.get(key)();
        }
        this.subscriptions.set(key, unsubscribe);
    }

    remove(key) {
        if (this.subscriptions.has(key)) {
            this.subscriptions.get(key)();
            this.subscriptions.delete(key);
        }
    }

    removeAll() {
        this.subscriptions.forEach(unsubscribe => unsubscribe());
        this.subscriptions.clear();
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// VENUES SERVICE CLASS
// ═══════════════════════════════════════════════════════════════════════════════

class VenuesService {
    constructor() {
        this.cache = new CacheManager();
        this.subscriptions = new SubscriptionManager();
    }

    // ───────────────────────────────────────────────────────────────────────────
    // UTILITY METHODS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Retry logic for failed operations
     */
    async retryOperation(operation, retries = MAX_RETRIES) {
        let lastError;
        for (let i = 0; i < retries; i++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error;
                if (i < retries - 1) {
                    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (i + 1)));
                }
            }
        }
        throw lastError;
    }

    /**
     * Validate venue data
     */
    validateVenueData(data) {
        const errors = [];

        if (!data.name || typeof data.name !== 'string' || !data.name.trim()) {
            errors.push('Venue name is required');
        }

        if (data.date && !/^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
            errors.push('Invalid date format (use YYYY-MM-DD)');
        }

        if (data.startingCash !== undefined && (isNaN(data.startingCash) || data.startingCash < 0)) {
            errors.push('Starting cash must be a positive number');
        }

        // Validate recurrence if specified
        if (data.recurrence && data.recurrence.pattern !== RECURRENCE_PATTERN.NONE) {
            if (!data.recurrence.endDate) {
                errors.push('Recurrence end date is required for recurring venues');
            }
            if (data.recurrence.pattern === RECURRENCE_PATTERN.WEEKLY && 
                (!data.recurrence.daysOfWeek || data.recurrence.daysOfWeek.length === 0)) {
                errors.push('At least one day of week must be selected for weekly recurrence');
            }
        }

        if (errors.length > 0) {
            throw new Error(`Validation failed: ${errors.join(', ')}`);
        }

        return true;
    }

    /**
     * Sanitize venue data before saving
     */
    sanitizeVenueData(data) {
        return {
            name: String(data.name || '').trim(),
            location: String(data.location || '').trim(),
            address: String(data.address || '').trim(),
            date: data.date ? String(data.date).trim() : null,
            startingCash: parseFloat(data.startingCash) || 0,
            venueType: data.venueType || VENUE_TYPE.REGULAR_MARKET,
            coverImageUrl: data.coverImageUrl || null,
            coverImageStoragePath: data.coverImageStoragePath || null,
            status: data.status || VENUE_STATUS.PLANNED,
            contact: String(data.contact || '').trim(),
            notes: String(data.notes || '').trim(),
            setupTasks: Array.isArray(data.setupTasks) ? data.setupTasks : [],
            midDayTasks: Array.isArray(data.midDayTasks) ? data.midDayTasks : [],
            breakdownTasks: Array.isArray(data.breakdownTasks) ? data.breakdownTasks : [],
            recurrence: data.recurrence || { pattern: RECURRENCE_PATTERN.NONE },
            isRecurringInstance: data.isRecurringInstance || false,
            recurringParentId: data.recurringParentId || null,
            recurringDays: Array.isArray(data.recurringDays) ? data.recurringDays : [],
            scheduleEndDate: data.scheduleEndDate ? String(data.scheduleEndDate).trim() : null,
            settings: data.settings || {},
            metadata: data.metadata || {}
        };
    }

    /**
     * Upload venue cover image
     */
    async uploadCoverImage(venueId, file) {
        if (!venueId || !file) {
            throw new Error('Venue ID and file are required');
        }

        try {
            // Create a reference to the file location
            const fileExtension = file.name.split('.').pop();
            const storagePath = `marketCovers/${venueId}/cover.${fileExtension}`;
            const storageReference = ref(storage, storagePath);

            console.log(`📸 Uploading cover image to:`, storagePath);

            // Upload file
            await uploadBytes(storageReference, file);

            // Get download URL
            const coverImageUrl = await getDownloadURL(storageReference);

            console.log(`✅ Cover image uploaded successfully:`, coverImageUrl);

            // Update venue document with both URL and storage path
            await updateDoc(doc(db, COLLECTIONS.VENUES, venueId), {
                coverImageUrl,
                coverImageStoragePath: storagePath,
                updatedAt: serverTimestamp()
            });

            // Clear cache
            this.cache.delete(`venue_${venueId}`);
            this.cache.clear();

            return { success: true, coverImageUrl, storagePath };
        } catch (error) {
            console.error('Error uploading cover image:', error);
            throw new Error(`Failed to upload cover image: ${error.message}`);
        }
    }

    // ───────────────────────────────────────────────────────────────────────────
    // VENUE OPERATIONS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Get all venues (with optional filtering)
     */
    async getVenues(options = {}) {
        const cacheKey = `venues_${JSON.stringify(options)}`;
        
        // Check cache
        if (!options.skipCache && this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            let q = query(collection(db, COLLECTIONS.VENUES));

            // Apply filters
            if (options.status) {
                q = query(q, where('status', '==', options.status));
            }

            // Apply ordering
            q = query(q, orderBy(options.orderBy || 'name', options.orderDirection || 'asc'));

            // Apply limit
            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const snapshot = await getDocs(q);
            const venues = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null,
                updatedAt: doc.data().updatedAt?.toDate?.() || null
            }));

            // Cache results
            this.cache.set(cacheKey, venues);

            return venues;
        } catch (error) {
            console.error('Error fetching venues:', error);
            throw new Error(`Failed to fetch venues: ${error.message}`);
        }
    }

    /**
     * Get a single venue by ID
     * @param {string} venueId
     * @param {{ bypassCache?: boolean }} options - bypassCache: true to always read from Firestore (e.g. before regenerating instances)
     */
    async getVenue(venueId, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const cacheKey = `venue_${venueId}`;

        if (!options.bypassCache && this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            const docRef = doc(db, COLLECTIONS.VENUES, venueId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                throw new Error('Venue not found');
            }

            const venue = {
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate?.() || null,
                updatedAt: docSnap.data().updatedAt?.toDate?.() || null
            };

            if (!options.bypassCache) this.cache.set(cacheKey, venue);

            return venue;
        } catch (error) {
            console.error('Error fetching venue:', error);
            throw new Error(`Failed to fetch venue: ${error.message}`);
        }
    }

    /**
     * Create a new venue (with optional recurring support)
     */
    async createVenue(data, options = {}) {
        try {
            // Validate
            this.validateVenueData(data);

            // Sanitize
            const sanitizedData = this.sanitizeVenueData(data);

            // Check if this is a recurring venue
            if (sanitizedData.recurrence && 
                sanitizedData.recurrence.pattern !== RECURRENCE_PATTERN.NONE) {
                
                return await this.createRecurringVenue(sanitizedData, options);
            }

            // Create single venue document
            const docRef = await addDoc(collection(db, COLLECTIONS.VENUES), {
                ...sanitizedData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // Clear cache
            this.cache.clear();

            // Generate weekly instances if configured (use local date for start to avoid timezone shifting weekdays)
            if (sanitizedData.recurringDays && sanitizedData.recurringDays.length > 0 && sanitizedData.scheduleEndDate) {
                const now = new Date();
                const y = now.getFullYear(), m = now.getMonth();
                const startISO = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                await this.generateMarketInstances(docRef.id, sanitizedData.recurringDays, startISO, sanitizedData.scheduleEndDate);
            }

            // Fetch and return the created venue
            return await this.getVenue(docRef.id);
        } catch (error) {
            console.error('Error creating venue:', error);
            throw new Error(`Failed to create venue: ${error.message}`);
        }
    }

    /**
     * Create recurring venue instances
     */
    async createRecurringVenue(data, options = {}) {
        try {
            const { recurrence } = data;
            const instances = [];

            // Create the parent venue
            const parentData = {
                ...data,
                isRecurringParent: true,
                status: VENUE_STATUS.PLANNED
            };

            const parentRef = await addDoc(collection(db, COLLECTIONS.VENUES), {
                ...parentData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            const parentId = parentRef.id;

            // Generate recurring instances
            const startDate = new Date(data.date);
            const endDate = new Date(recurrence.endDate);

            let currentDate = new Date(startDate);
            const batch = writeBatch(db);
            let batchCount = 0;

            while (currentDate <= endDate) {
                // Check if we should create an instance for this date
                if (this.shouldCreateInstance(currentDate, recurrence)) {
                    const instanceData = {
                        ...data,
                        date: currentDate.toISOString().slice(0, 10),
                        isRecurringInstance: true,
                        recurringParentId: parentId,
                        recurrence: null // Instances don't have recurrence
                    };

                    const instanceRef = doc(collection(db, COLLECTIONS.VENUES));
                    batch.set(instanceRef, {
                        ...instanceData,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    });

                    instances.push({
                        id: instanceRef.id,
                        date: instanceData.date
                    });

                    batchCount++;

                    // Firestore batch limit is 500, commit and start new batch
                    if (batchCount >= 450) {
                        await batch.commit();
                        batchCount = 0;
                    }
                }

                // Move to next date based on pattern
                currentDate = this.getNextDate(currentDate, recurrence);
            }

            // Commit remaining instances
            if (batchCount > 0) {
                await batch.commit();
            }

            // Update parent with instance count
            await updateDoc(doc(db, COLLECTIONS.VENUES, parentId), {
                instanceCount: instances.length,
                instances: instances.map(i => ({ id: i.id, date: i.date }))
            });

            // Clear cache
            this.cache.clear();

            return {
                parentId,
                instanceCount: instances.length,
                instances: instances.slice(0, 10) // Return first 10
            };
        } catch (error) {
            console.error('Error creating recurring venue:', error);
            throw new Error(`Failed to create recurring venue: ${error.message}`);
        }
    }

    /**
     * Check if an instance should be created for a given date
     */
    shouldCreateInstance(date, recurrence) {
        const dayOfWeek = date.getDay();

        switch (recurrence.pattern) {
            case RECURRENCE_PATTERN.DAILY:
                return true;

            case RECURRENCE_PATTERN.WEEKLY:
                return recurrence.daysOfWeek && 
                       recurrence.daysOfWeek.includes(dayOfWeek);

            case RECURRENCE_PATTERN.BI_WEEKLY:
                // Check if it's the correct week
                const startDate = new Date(recurrence.startDate || date);
                const weeksDiff = Math.floor((date - startDate) / (7 * 24 * 60 * 60 * 1000));
                return weeksDiff % 2 === 0 && 
                       recurrence.daysOfWeek && 
                       recurrence.daysOfWeek.includes(dayOfWeek);

            case RECURRENCE_PATTERN.MONTHLY:
                // Same day of month
                const targetDay = recurrence.dayOfMonth || date.getDate();
                return date.getDate() === targetDay;

            default:
                return false;
        }
    }

    /**
     * Get next date based on recurrence pattern
     */
    getNextDate(currentDate, recurrence) {
        const nextDate = new Date(currentDate);

        switch (recurrence.pattern) {
            case RECURRENCE_PATTERN.DAILY:
                nextDate.setDate(nextDate.getDate() + 1);
                break;

            case RECURRENCE_PATTERN.WEEKLY:
            case RECURRENCE_PATTERN.BI_WEEKLY:
                nextDate.setDate(nextDate.getDate() + 1);
                break;

            case RECURRENCE_PATTERN.MONTHLY:
                nextDate.setMonth(nextDate.getMonth() + 1);
                break;

            default:
                nextDate.setDate(nextDate.getDate() + 1);
        }

        return nextDate;
    }

    // ───────────────────────────────────────────────────────────────────────────
    // MARKET INSTANCES (simple weekly recurrence)
    // ───────────────────────────────────────────────────────────────────────────

    async generateMarketInstances(marketId, recurringDays, startDateISO, endDateISO) {
        if (!marketId || !recurringDays || recurringDays.length === 0 || !endDateISO) {
            return { success: false, reason: 'missing-params' };
        }

        const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
        const normalizeDay = (d) => {
            const s = String(d).toLowerCase().trim();
            const three = s.slice(0, 3);
            if (dayMap[three] !== undefined) return dayMap[three];
            const full = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
            return full[s];
        };
        const targetDaysSet = new Set(
            recurringDays.map(normalizeDay).filter((n) => n !== undefined && n >= 0 && n <= 6)
        );
        if (targetDaysSet.size === 0) return { success: false, reason: 'no-valid-days' };

        const start = new Date(startDateISO + 'T12:00:00');
        const end = new Date(endDateISO + 'T12:00:00');
        if (isNaN(start.getTime()) || isNaN(end.getTime())) throw new Error('Invalid start or end date');

        const todayMid = new Date();
        todayMid.setHours(0, 0, 0, 0);
        const backfill = new Date(todayMid);
        backfill.setDate(backfill.getDate() - 7);
        const pivot = new Date(start < backfill ? backfill : (start < todayMid ? todayMid : start));
        pivot.setHours(0, 0, 0, 0);
        const endMid = new Date(end);
        endMid.setHours(0, 0, 0, 0);
        const pivotISO = `${pivot.getFullYear()}-${String(pivot.getMonth() + 1).padStart(2, '0')}-${String(pivot.getDate()).padStart(2, '0')}`;
        const endISO = `${endMid.getFullYear()}-${String(endMid.getMonth() + 1).padStart(2, '0')}-${String(endMid.getDate()).padStart(2, '0')}`;

        const deleteRangeEnd = new Date(todayMid);
        deleteRangeEnd.setFullYear(deleteRangeEnd.getFullYear() + 1);
        const deleteRangeEndISO = `${deleteRangeEnd.getFullYear()}-${String(deleteRangeEnd.getMonth() + 1).padStart(2, '0')}-${String(deleteRangeEnd.getDate()).padStart(2, '0')}`;

        const existing = await this.getMarketInstances({ marketId, startDate: pivotISO, endDate: deleteRangeEndISO });
        const toDelete = existing.filter((i) => {
            const dateStr = String(i.date);
            if (dateStr > endISO) return true;
            const weekday = new Date(dateStr + 'T12:00:00').getDay();
            return !targetDaysSet.has(weekday);
        });
        if (toDelete.length > 0) {
            let delBatch = writeBatch(db);
            let delCount = 0;
            for (const i of toDelete) {
                delBatch.delete(doc(db, COLLECTIONS.MARKET_INSTANCES, i.id));
                delCount++;
                if (delCount >= 450) {
                    await delBatch.commit();
                    delBatch = writeBatch(db);
                    delCount = 0;
                }
            }
            if (delCount > 0) await delBatch.commit();
        }

        let batch = writeBatch(db);
        let toCommit = 0;

        for (let d = new Date(pivot); d <= endMid; d.setDate(d.getDate() + 1)) {
            const y = d.getFullYear(), m = d.getMonth(), day = d.getDate();
            const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const weekday = new Date(iso + 'T12:00:00').getDay();
            if (!targetDaysSet.has(weekday)) continue;
            const id = `${marketId}_${iso}`;
            const ref = doc(collection(db, COLLECTIONS.MARKET_INSTANCES), id);
            batch.set(ref, {
                id,
                marketId,
                date: iso,
                status: VENUE_STATUS.PLANNED,
                staffingRequired: 0,
                staffingAssigned: [],
                stockPrepared: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            }, { merge: true });
            toCommit++;
            if (toCommit >= 450) {
                await batch.commit();
                batch = writeBatch(db);
                toCommit = 0;
            }
        }

        if (toCommit > 0) await batch.commit();
        return { success: true };
    }

    async getMarketInstances({ marketId, startDate, endDate, limitCount }) {
        // Avoid composite-index requirements by querying date range only, then filter marketId client-side
        let q = query(collection(db, COLLECTIONS.MARKET_INSTANCES));
        if (startDate) q = query(q, where('date', '>=', startDate));
        if (endDate) q = query(q, where('date', '<=', endDate));
        q = query(q, orderBy('date', 'asc'));
        if (limitCount) q = query(q, limit(limitCount));
        const snapshot = await getDocs(q);
        const all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        return marketId ? all.filter(i => i.marketId === marketId) : all;
    }

    async updateMarketInstance(id, data) {
        const ref = doc(db, COLLECTIONS.MARKET_INSTANCES, id);
        await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
        return { success: true };
    }

    /**
     * Regenerate market instances for the current month for a venue.
     * Safe to call multiple times – generation is idempotent (merge=true).
     */
    async regenerateInstancesForCurrentMonth(venueId) {
        const venue = await this.getVenue(venueId, { bypassCache: true });
        if (!venue || !Array.isArray(venue.recurringDays) || venue.recurringDays.length === 0 || !venue.scheduleEndDate) {
            return { success: false, reason: 'no-recurrence' };
        }
        const now = new Date();
        const y = now.getFullYear(), m = now.getMonth();
        const startISO = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        await this.generateMarketInstances(venueId, venue.recurringDays, startISO, venue.scheduleEndDate);
        return { success: true };
    }

    /**
     * Get recurring venue instances
     */
    async getRecurringInstances(parentId, options = {}) {
        if (!parentId) {
            throw new Error('Parent venue ID is required');
        }

        try {
            let q = query(
                collection(db, COLLECTIONS.VENUES),
                where('recurringParentId', '==', parentId),
                orderBy('date', 'asc')
            );

            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null,
                updatedAt: doc.data().updatedAt?.toDate?.() || null
            }));
        } catch (error) {
            console.error('Error fetching recurring instances:', error);
            throw new Error(`Failed to fetch recurring instances: ${error.message}`);
        }
    }

    /**
     * Update a venue
     */
    async updateVenue(venueId, data, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        try {
            // If fullUpdate is true, sanitize all data
            // Otherwise, only update the fields that are provided (partial update)
            let updateData;
            
            if (options.fullUpdate) {
                // Full update - sanitize all data
                updateData = this.sanitizeVenueData(data);
            } else {
                // Partial update - only update provided fields
                updateData = {};
                
                // Only include fields that are explicitly provided
                if (data.name !== undefined) updateData.name = String(data.name).trim();
                if (data.location !== undefined) updateData.location = String(data.location).trim();
                if (data.address !== undefined) updateData.address = String(data.address).trim();
                if (data.date !== undefined) updateData.date = data.date ? String(data.date).trim() : null;
                if (data.startingCash !== undefined) updateData.startingCash = parseFloat(data.startingCash) || 0;
                if (data.venueType !== undefined) updateData.venueType = data.venueType;
                if (data.coverImageUrl !== undefined) updateData.coverImageUrl = data.coverImageUrl;
                if (data.coverImageStoragePath !== undefined) updateData.coverImageStoragePath = data.coverImageStoragePath;
                if (data.status !== undefined) updateData.status = data.status;
                if (data.contact !== undefined) updateData.contact = String(data.contact).trim();
                if (data.notes !== undefined) updateData.notes = String(data.notes).trim();
                if (data.setupTasks !== undefined) updateData.setupTasks = Array.isArray(data.setupTasks) ? data.setupTasks : [];
                if (data.midDayTasks !== undefined) updateData.midDayTasks = Array.isArray(data.midDayTasks) ? data.midDayTasks : [];
                if (data.breakdownTasks !== undefined) updateData.breakdownTasks = Array.isArray(data.breakdownTasks) ? data.breakdownTasks : [];
                if (data.recurrence !== undefined) updateData.recurrence = data.recurrence;
                if (data.isRecurringInstance !== undefined) updateData.isRecurringInstance = data.isRecurringInstance;
                if (data.recurringParentId !== undefined) updateData.recurringParentId = data.recurringParentId;
                if (data.settings !== undefined) updateData.settings = data.settings;
                if (data.metadata !== undefined) updateData.metadata = data.metadata;
                if (data.recurringDays !== undefined) updateData.recurringDays = Array.isArray(data.recurringDays) ? data.recurringDays : [];
                if (data.scheduleEndDate !== undefined) updateData.scheduleEndDate = data.scheduleEndDate ? String(data.scheduleEndDate).trim() : null;
            }

            // Update document
            const docRef = doc(db, COLLECTIONS.VENUES, venueId);
            await updateDoc(docRef, {
                ...updateData,
                updatedAt: serverTimestamp()
            });

            // Clear cache
            this.cache.delete(`venue_${venueId}`);
            this.cache.clear(); // Clear all venue queries

            // Optionally regenerate instances if fields changed (use local date for start)
            if (data.recurringDays !== undefined || data.scheduleEndDate !== undefined) {
                const updated = await this.getVenue(venueId);
                if (updated.recurringDays && updated.recurringDays.length > 0 && updated.scheduleEndDate) {
                    const now = new Date();
                    const y = now.getFullYear(), m = now.getMonth();
                    const startISO = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                    await this.generateMarketInstances(venueId, updated.recurringDays, startISO, updated.scheduleEndDate);
                }
                return updated;
            }

            // Fetch and return the updated venue
            return await this.getVenue(venueId);
        } catch (error) {
            console.error('Error updating venue:', error);
            throw new Error(`Failed to update venue: ${error.message}`);
        }
    }

    /**
     * Delete a venue
     */
    async deleteVenue(venueId) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        try {
            // Check if venue has associated data
            const shifts = await this.getShiftsByVenue(venueId, { limit: 1 });
            if (shifts.length > 0) {
                throw new Error('Cannot delete venue with existing shifts. Please delete or reassign shifts first.');
            }

            const stock = await this.getVenueStock(venueId, { limit: 1 });
            if (stock.length > 0) {
                throw new Error('Cannot delete venue with existing stock. Please delete stock first.');
            }

            // Delete venue
            await deleteDoc(doc(db, COLLECTIONS.VENUES, venueId));

            // Clear cache
            this.cache.delete(`venue_${venueId}`);
            this.cache.clear();

            return { success: true, venueId };
        } catch (error) {
            console.error('Error deleting venue:', error);
            throw new Error(`Failed to delete venue: ${error.message}`);
        }
    }

    /**
     * Subscribe to venues changes
     */
    subscribeToVenues(callback, options = {}) {
        let q = query(collection(db, COLLECTIONS.VENUES));

        // Apply filters
        if (options.status) {
            q = query(q, where('status', '==', options.status));
        }

        // Apply ordering
        q = query(q, orderBy(options.orderBy || 'name', options.orderDirection || 'asc'));

        const subscriptionKey = `venues_${JSON.stringify(options)}`;

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const venues = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.() || null,
                    updatedAt: doc.data().updatedAt?.toDate?.() || null
                }));

                // Update cache
                this.cache.set(subscriptionKey, venues);

                callback(venues);
            },
            (error) => {
                console.error('Error in venues subscription:', error);
                callback(null, error);
            }
        );

        this.subscriptions.add(subscriptionKey, unsubscribe);

        return unsubscribe;
    }

    /**
     * Subscribe to a single venue
     */
    subscribeToVenue(venueId, callback) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const subscriptionKey = `venue_${venueId}`;

        const unsubscribe = onSnapshot(
            doc(db, COLLECTIONS.VENUES, venueId),
            (docSnap) => {
                if (!docSnap.exists()) {
                    callback(null, new Error('Venue not found'));
                    return;
                }

                const venue = {
                    id: docSnap.id,
                    ...docSnap.data(),
                    createdAt: docSnap.data().createdAt?.toDate?.() || null,
                    updatedAt: docSnap.data().updatedAt?.toDate?.() || null
                };

                // Update cache
                this.cache.set(subscriptionKey, venue);

                callback(venue);
            },
            (error) => {
                console.error('Error in venue subscription:', error);
                callback(null, error);
            }
        );

        this.subscriptions.add(subscriptionKey, unsubscribe);

        return unsubscribe;
    }

    // ───────────────────────────────────────────────────────────────────────────
    // VENUE STOCK OPERATIONS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Get stock items for a venue
     */
    async getVenueStock(venueId, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const cacheKey = `stock_${venueId}_${JSON.stringify(options)}`;

        // Check cache
        if (!options.skipCache && this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            let q = query(
                collection(db, COLLECTIONS.VENUE_STOCK),
                where('venueId', '==', venueId)
            );

            // Apply ordering
            q = query(q, orderBy(options.orderBy || 'name', options.orderDirection || 'asc'));

            // Apply limit
            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const snapshot = await getDocs(q);
            const stock = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null,
                updatedAt: doc.data().updatedAt?.toDate?.() || null
            }));

            // Cache results
            this.cache.set(cacheKey, stock);

            return stock;
        } catch (error) {
            console.error('Error fetching venue stock:', error);
            throw new Error(`Failed to fetch venue stock: ${error.message}`);
        }
    }

    /**
     * Subscribe to venue stock changes
     */
    subscribeToVenueStock(venueId, callback) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const subscriptionKey = `stock_${venueId}`;

        const unsubscribe = onSnapshot(
            query(
                collection(db, COLLECTIONS.VENUE_STOCK),
                where('venueId', '==', venueId),
                orderBy('name')
            ),
            (snapshot) => {
                const stock = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.() || null,
                    updatedAt: doc.data().updatedAt?.toDate?.() || null
                }));

                // Update cache
                this.cache.set(subscriptionKey, stock);

                callback(stock);
            },
            (error) => {
                console.error('Error in stock subscription:', error);
                callback(null, error);
            }
        );

        this.subscriptions.add(subscriptionKey, unsubscribe);

        return unsubscribe;
    }

    // ───────────────────────────────────────────────────────────────────────────
    // VENUE DETAILS & AGGREGATION
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Get comprehensive venue details with all associated data
     */
    async getVenueDetails(venueId) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        try {
            // Get base venue
            const venue = await this.getVenue(venueId);

            // Get associated shifts
            const shifts = await this.getShiftsByVenue(venueId);

            // Get stock items
            const stock = await this.getVenueStock(venueId);

            // Calculate stats
            const stats = {
                totalShifts: shifts.length,
                confirmedShifts: shifts.filter(s => s.status === 'confirmed').length,
                totalStock: stock.length,
                lowStockItems: stock.filter(item => item.currentStock < item.minimumLevel).length,
                totalStaffAssigned: new Set(shifts.flatMap(s => s.staffIds || [])).size
            };

            // Get recurring instances if this is a parent
            let instances = [];
            if (venue.isRecurringParent) {
                instances = await this.getRecurringInstances(venueId, { limit: 20 });
            }

            return {
                ...venue,
                shifts,
                stock,
                stats,
                instances
            };
        } catch (error) {
            console.error('Error getting venue details:', error);
            throw new Error(`Failed to get venue details: ${error.message}`);
        }
    }

    /**
     * Get shifts for a venue
     */
    async getShiftsByVenue(venueId, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        try {
            let q = query(
                collection(db, COLLECTIONS.VENUE_SHIFTS),
                where('venueId', '==', venueId)
            );

            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error('Error fetching shifts:', error);
            throw new Error(`Failed to fetch shifts: ${error.message}`);
        }
    }

    /**
     * Update venue status
     */
    async updateVenueStatus(venueId, status) {
        if (!venueId || !status) {
            throw new Error('Venue ID and status are required');
        }

        if (!Object.values(VENUE_STATUS).includes(status)) {
            throw new Error('Invalid venue status');
        }

        return await this.updateVenue(venueId, { status });
    }

    // ───────────────────────────────────────────────────────────────────────────
    // CLEANUP
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Cleanup all subscriptions and cache
     */
    cleanup() {
        this.subscriptions.removeAll();
        this.cache.clear();
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE
// ═══════════════════════════════════════════════════════════════════════════════

const venuesServiceInstance = new VenuesService();

export default venuesServiceInstance;

// Export the class for testing
export { VenuesService };

